import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Lock, Mail, PencilLine, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SkeletonRows } from "@/components/app/Common";
import { confirmAction } from "@/components/app/Confirm";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { COMP_LABELS } from "@/lib/fit";

const ago = (d) => formatDistanceToNow(new Date(d), { addSuffix: true });

function Notes({ appId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [vis, setVis] = useState("team");
  const { data = [] } = useQuery({ queryKey: ["notes", appId], queryFn: () => get(`/applications/${appId}/notes`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["notes", appId] });
  const add = async () => { try { await api.post(`/applications/${appId}/notes`, { body, visibility: vis }); setBody(""); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { try { await api.delete(`/notes/${id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="panel space-y-3 p-5" data-tour="pt-notes-panel" data-testid="notes-panel">
      <h3 className="text-base font-semibold">Team notes</h3>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note for the hiring team…" data-tour="pt-note-input" data-testid="note-input" />
      <div className="flex items-center gap-2">
        <select value={vis} onChange={(e) => setVis(e.target.value)} className="h-8 rounded-lg border border-line bg-white px-2 text-xs" data-testid="note-visibility"><option value="team">Visible to the team</option><option value="private">Only me</option></select>
        <Button size="sm" className="ml-auto" disabled={!body.trim()} onClick={add} data-testid="note-add-btn">Add note</Button>
      </div>
      {data.map((n) => (
        <div key={n.id} className="rounded-[5px] border border-line p-3 text-sm" data-testid={`note-${n.id}`}>
          <p className="whitespace-pre-wrap">{n.body}</p>
          <p className="mt-1 flex items-center gap-2 text-xs text-ink-muted">{n.visibility === "private" && <Lock size={11} />}{n.author_name || n.author_email} · {ago(n.created_at)}
            {(n.author_id === user.id || user.role === "super_admin") && <button onClick={async () => (await confirmAction({ title: "Delete this note?", body: "The note is removed for everyone. This can't be undone.", entity: `"${(n.body || "").slice(0, 80)}"`, confirmLabel: "Delete note" })) && del(n.id)} className="ml-auto hover:text-risk" data-testid={`note-delete-${n.id}`}><Trash2 size={12} /></button>}</p>
        </div>
      ))}
    </section>
  );
}

const ICON = { decision: History, score_overridden: PencilLine, category_overridden: PencilLine, email_sent: Mail };

export function ActivityTab({ c }) {
  const [appId, setAppId] = useState(c.applications[0]?.id);
  const { data, isLoading } = useQuery({ queryKey: ["activity", appId], queryFn: () => get(`/applications/${appId}/activity`), enabled: !!appId });
  if (!appId) return null;
  return (
    <div className="space-y-4" data-testid="activity-tab">
      {c.applications.length > 1 && <select value={appId} onChange={(e) => setAppId(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2 text-sm">{c.applications.map((a) => <option key={a.id} value={a.id}>{a.job_title}</option>)}</select>}
      <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
        <Notes appId={appId} />
        {isLoading ? <SkeletonRows rows={5} /> : (
          <div className="space-y-5">
            <section className="panel p-5" data-tour="pt-stage-timeline" data-testid="stage-timeline"><h3 className="mb-3 text-base font-semibold">Timeline</h3>
              <ol className="relative space-y-3 border-l border-line pl-4">{data.events.map((e) => { const I = ICON[e.type] || History; return (
                <li key={e.id} className="text-sm"><span className="absolute -left-[7px] mt-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white ring-1 ring-line"><I size={8} className="text-brand" /></span>
                  <p className="text-ink">{e.summary}</p><p className="text-xs text-ink-muted">{e.actor_email} · {ago(e.created_at)}{e.data?.reason ? ` · “${e.data.reason}”` : ""}</p></li>); })}
                {!data.events.length && <li className="text-sm text-ink-muted">No activity yet.</li>}</ol></section>
            <section className="panel p-5" data-testid="overrides-list"><h3 className="mb-2 text-base font-semibold">Overrides</h3>
              {data.overrides.length ? data.overrides.map((o) => <p key={o.component} className="text-sm text-ink-secondary"><strong className="text-ink">{COMP_LABELS[o.component] || "Category"}</strong> → {o.value} · {o.by}: {o.reason}</p>) : <p className="text-sm text-ink-muted">No overrides.</p>}</section>
            <section className="panel p-5" data-tour="pt-emails-list" data-testid="emails-list"><h3 className="mb-2 text-base font-semibold">Emails sent</h3>
              {data.emails.length ? data.emails.map((e) => <p key={e.id} className="flex justify-between gap-3 text-sm"><span className="truncate">{e.subject}</span><span className="shrink-0 text-xs text-ink-muted">{e.status} · {ago(e.created_at)}</span></p>) : <p className="text-sm text-ink-muted">No emails yet.</p>}</section>
          </div>
        )}
      </div>
    </div>
  );
}
